/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * Checks the local storage for the given key and if it is a token (not some bad XSS script) will return
 * the token. Otherwise, will return null and throw a console error.
 * @param key
 */
export function getTokenFromLocalStorage(key: string): string | undefined {
    let potentialToken = localStorage.getItem(key);
    let badMatches: any[] | null = null;

    if (potentialToken) {
        // This regexp looks for any non-JWT expected characters. This is looking for symbols that could be part of an XSS attack.
        badMatches = potentialToken.match(/[^\w\.\-_]/g);
    }

    // null potentialToken means the token doesn't exist, having any badMatches strings in the array means
    // there is a non-JWT approved character in the token, it should NOT be used.
    if (!potentialToken || badMatches) {
        return undefined;
    }

    return potentialToken;
}

/**
 * Checks if the provided access token is expired or not.
 * @param {string} token - The access token.
 * @returns {boolean} - Returns true if the token is expired, false otherwise.
 * @throws {Error} - Throws an error if the token cannot be parsed.
 */
export function isAccessTokenExpired(token) {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        const { exp: expirationTime } = JSON.parse(jsonPayload);
        const currentTime = new Date();
        currentTime.setMinutes(currentTime.getMinutes() + 1);
        const currentTimeWithMinuteAdded = Math.floor(currentTime / 1000); // Convert to seconds
        return expirationTime < currentTimeWithMinuteAdded;
    } catch (e) {
        console.error("Unable to parse JWT token", e);
        return false;
    }
}